import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Search, Fingerprint, FileText, Eye } from 'lucide-react';
import { useNodeClickHandler } from './useNodeClickHandler';

// Map clue types to icons
const getClueIcon = (type) => {
  const typeIcons = {
    physical: Fingerprint,
    document: FileText,
    testimony: Eye,
    default: Search,
  };
  return typeIcons[type?.toLowerCase()] || typeIcons.default;
};

export const ClueNode = memo(({ data, selected }) => {
  const Icon = getClueIcon(data.type);

  const handleClick = useNodeClickHandler(
    data.onViewDetails,
    () => ({
      type: 'clue',
      name: data.label,
      description: data.description,
      clueType: data.type,
      location: data.location,
      significance: data.significance,
    }),
    data.isDraggingRef
  );

  return (
    <div
      className={`
        relative p-4 rounded-lg shadow-lg min-w-[180px] max-w-[220px] cursor-pointer
        bg-yellow-950 border-2 border-yellow-600
        ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent' : ''}
        transition-all duration-200 hover:scale-105
      `}
      onClick={handleClick}
    >
      {/* Connection handles - larger hit area for easier connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-yellow-400 !border-2 !border-yellow-600 hover:!bg-yellow-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-yellow-400 !border-2 !border-yellow-600 hover:!bg-yellow-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-4 !h-4 !bg-yellow-400 !border-2 !border-yellow-600 hover:!bg-yellow-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-4 !h-4 !bg-yellow-400 !border-2 !border-yellow-600 hover:!bg-yellow-300 hover:!scale-125 transition-transform"
      />

      {/* Content */}
      <div className="flex flex-col gap-2">
        {/* Header with icon */}
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-yellow-900">
            <Icon className="w-5 h-5 text-yellow-400" />
          </div>
          <span className="text-xs font-semibold text-yellow-400 uppercase">
            {data.type || 'Clue'}
          </span>
        </div>

        {/* Name */}
        <p className="text-base font-bold text-white leading-tight">
          {data.label}
        </p>

        {/* Description preview */}
        {data.description && (
          <p className="text-sm text-gray-300 line-clamp-3">
            {data.description}
          </p>
        )}

        {/* Location found */}
        {data.location && (
          <p className="text-xs text-gray-500 truncate">
            Found: {data.location}
          </p>
        )}
      </div>

      {/* Clue badge */}
      <div className="absolute -top-2 -right-2 bg-yellow-500 rounded-full p-1">
        <Search className="w-3 h-3 text-yellow-950" />
      </div>
    </div>
  );
});

ClueNode.displayName = 'ClueNode';

export default ClueNode;
